import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import { Button } from "../../components/ui/button";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { Option } from "../../interface/Option";
import { IcdNotAllowedIcdAxiosInstance } from "../../Network/IcdNotAllowedIcdAxiosInstance";
import { icdNotAllowedIcd, Datum } from "../../interface/IcdNotAllowed";
import Listofmedicalcode from "../Shared/ListofIcdmedicalcode";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import ListOfInsuranceplanName from "../Shared/ListOfInsuranceplanName";
import VipPatientSelector from "Pages/Shared/VipPatient";
import ListOfProviderName from "../Shared/ListOfProviderName";
import PolicyNames from "../Shared/PolicyNames";
import ListofMedicalGroup from "../Shared/ListofMedicalGroup";
import ListofCustomIcd from "../Shared/ListofcustemIcd";
import ListOfDoctorName from "../Shared/Doctor";
import FileUpload from "Pages/uploadFile/UploadFileIcdNotAllowed/UploadFileIcdNotAllowed";
import Vip from "../Shared/Vip";
import ListOfSpecialtyName from "../Shared/Specialty";
import { ScrollArea } from "../../components/ui/scroll-area";

export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  plans?: Array<{
    Plan_name?: string;
    plan_name?: string;
    name?: string;
    id?: number;
    [key: string]: any;
  }>;
}


// ─── QA Automation IDs ───────────────────────────────────────────────────────
// Stable selectors for Playwright / Cypress / Selenium.
// Additive only: no API, payload, validation logic, permissions, routing,
// visual style, element order, or UI behavior changes.
const QA_PAGE_PREFIX = "icd-not-allowed";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const qaId = (suffix: string | number) => `${QA_PAGE_PREFIX}-${qaSafe(suffix)}`;

const qaAttrs = (suffix: string | number) => ({
  id: qaId(suffix),
  "data-testid": qaId(suffix),
  "data-qa": qaId(suffix),
});

const rowQaId = (action: string, id: string | number | undefined) =>
  qaId(`${action}-${id ?? "unknown"}`);

// ─── Helper ───────────────────────────────────────────────────────────────────
const TagsWrap: React.FC<{ items: string[]; color: "blue" | "green" | "purple" | "gray"; automationId?: string }> = ({ items, color, automationId }) => {
  const styles: Record<string, React.CSSProperties> = {
    blue:   { background: "#eff6ff", color: "#1d4ed8", border: "0.5px solid #bfdbfe" },
    green:  { background: "#f0fdf4", color: "#15803d", border: "0.5px solid #bbf7d0" },
    purple: { background: "#faf5ff", color: "#7e22ce", border: "0.5px solid #e9d5ff" },
    gray:   { background: "#f8fafc", color: "#475569", border: "0.5px solid #e2e8f0" },
  };
  if (!items || !items.length)
    return (
      <span
        {...(automationId ? { id: `${automationId}-empty`, "data-testid": `${automationId}-empty`, "data-qa": `${automationId}-empty` } : {})}
        style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}
      >
        None
      </span>
    );
  return (
    <div
      {...(automationId ? { id: automationId, "data-testid": automationId, "data-qa": automationId } : {})}
      style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}
    >
      {items.map((item, i) => (
        <span
          key={i}
          {...(automationId ? { id: `${automationId}-item-${i}`, "data-testid": `${automationId}-item-${i}`, "data-qa": `${automationId}-item-${i}`, "data-qa-label": item } : {})}
          style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 9999, fontSize: 11, fontWeight: 500, ...styles[color] }}
        >
          {item}
        </span>
      ))}
    </div>
  );
};

const preserveScrollCheckbox = (e: React.ChangeEvent<HTMLInputElement>, fn: () => void) => {
  const el = e.currentTarget as HTMLElement;
  const dialog = el.closest('[role="dialog"]');
  const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
  const scrollTop = scrollable?.scrollTop ?? 0;
  fn();
  requestAnimationFrame(() => {
    if (scrollable) scrollable.scrollTop = scrollTop;
  });
};

const modeToCard = (mode: string | undefined): { cardMode: "EXCLUSION" | "INCLUSION" | null; isActive: boolean } => {
  if (mode === "EXCLUDED") return { cardMode: "EXCLUSION", isActive: true };
  if (mode === "INCLUDED") return { cardMode: "INCLUSION", isActive: true };
  return { cardMode: null, isActive: false };
};

const cardToMode = (cardMode: "EXCLUSION" | "INCLUSION" | null): string => {
  if (cardMode === "EXCLUSION") return "EXCLUDED";
  if (cardMode === "INCLUSION") return "INCLUDED";
  return "DISABLED";
};

interface EntityCardProps {
  label: string;
  icon: string;
  color: string;
  isActive: boolean;
  mode: "EXCLUSION" | "INCLUSION" | null;
  onExclusion: () => void;
  onInclusion: () => void;
  onClear: () => void;
  automationId?: string;
  children: React.ReactNode;
}

const colors: Record<string, { border: string; bg: string; text: string; badgeBg: string }> = {
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    text: "text-blue-700",    badgeBg: "bg-blue-50 text-blue-700 border-blue-200" },
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50",    text: "text-cyan-700",    badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  teal:    { border: "border-teal-200",    bg: "bg-teal-50",    text: "text-teal-700",    badgeBg: "bg-teal-50 text-teal-700 border-teal-200" },
  purple:  { border: "border-purple-200",  bg: "bg-purple-50",  text: "text-purple-700",  badgeBg: "bg-purple-50 text-purple-700 border-purple-200" },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-700",   badgeBg: "bg-amber-50 text-amber-700 border-amber-200" },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pink:    { border: "border-pink-200",    bg: "bg-pink-50",    text: "text-pink-700",    badgeBg: "bg-pink-50 text-pink-700 border-pink-200" },
  yellow:  { border: "border-yellow-200",  bg: "bg-yellow-50",  text: "text-yellow-700",  badgeBg: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50",  text: "text-indigo-700",  badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  slate:   { border: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-700",   badgeBg: "bg-slate-50 text-slate-700 border-slate-200" },
};

const EntityCard: React.FC<EntityCardProps> = ({
  label, icon, color, isActive, mode, onExclusion, onInclusion, onClear, automationId, children,
}) => {
  const c = colors[color] ?? colors.slate;

  const preserveScroll = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dialog = (e.currentTarget as HTMLElement).closest('[role="dialog"]');
    const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
    const scrollTop = scrollable?.scrollTop ?? 0;
    fn();
    requestAnimationFrame(() => {
      if (scrollable) scrollable.scrollTop = scrollTop;
    });
  };

  return (
    <div
      {...(automationId ? { id: automationId, "data-testid": automationId, "data-qa": automationId } : {})}
      data-qa-active={isActive ? "true" : "false"}
      data-qa-mode={mode ?? "DISABLED"}
      className={`bg-white rounded-xl border p-4 transition-all relative ${isActive ? `${c.border} shadow-sm` : "border-slate-100"}`}
    >
      {isActive && (
        <button
          type="button"
          {...(automationId ? { id: `${automationId}-clear`, "data-testid": `${automationId}-clear`, "data-qa": `${automationId}-clear` } : {})}
          data-qa-action="clear-entity-card"
          onClick={preserveScroll(onClear)}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10"
          title="Clear"
        >
          <i
            {...(automationId ? { id: `${automationId}-clear-icon`, "data-testid": `${automationId}-clear-icon`, "data-qa": `${automationId}-clear-icon` } : {})}
            className="fa-solid fa-xmark text-[14px]"
          ></i>
        </button>
      )}
      <div className="flex flex-col items-center gap-4">
        <span
          {...(automationId ? { id: `${automationId}-badge`, "data-testid": `${automationId}-badge`, "data-qa": `${automationId}-badge` } : {})}
          className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isActive ? c.badgeBg : "bg-slate-50 text-slate-400 border-slate-200"}`}
        >
          <i
            {...(automationId ? { id: `${automationId}-badge-icon`, "data-testid": `${automationId}-badge-icon`, "data-qa": `${automationId}-badge-icon` } : {})}
            className={`fa-solid ${icon} text-[12px]`}
          ></i>
          <span {...(automationId ? { id: `${automationId}-badge-label`, "data-testid": `${automationId}-badge-label`, "data-qa": `${automationId}-badge-label` } : {})}>
            {label}
          </span>
        </span>
        <div
          {...(automationId ? { id: `${automationId}-mode-selector`, "data-testid": `${automationId}-mode-selector`, "data-qa": `${automationId}-mode-selector` } : {})}
          className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner"
        >
          <button
            type="button"
            {...(automationId ? { id: `${automationId}-exclusion`, "data-testid": `${automationId}-exclusion`, "data-qa": `${automationId}-exclusion` } : {})}
            data-qa-action="select-exclusion"
            data-qa-selected={isActive && mode === "EXCLUSION" ? "true" : "false"}
            onClick={preserveScroll(onExclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Exclusion
          </button>
          <button
            type="button"
            {...(automationId ? { id: `${automationId}-inclusion`, "data-testid": `${automationId}-inclusion`, "data-qa": `${automationId}-inclusion` } : {})}
            data-qa-action="select-inclusion"
            data-qa-selected={isActive && mode === "INCLUSION" ? "true" : "false"}
            onClick={preserveScroll(onInclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Inclusion
          </button>
        </div>
      </div>
      {isActive && (
        <div
          {...(automationId ? { id: `${automationId}-content`, "data-testid": `${automationId}-content`, "data-qa": `${automationId}-content` } : {})}
          className="mt-4 pt-4 border-t border-slate-100 space-y-3"
        >
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Role Helper ──────────────────────────────────────────────────────────────
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};
// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; automationId?: string; children: React.ReactNode }> = ({ num, title, subtitle, automationId, children }) => (
  <div {...(automationId ? { id: automationId, "data-testid": automationId, "data-qa": automationId } : {})} className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div
      {...(automationId ? { id: `${automationId}-header`, "data-testid": `${automationId}-header`, "data-qa": `${automationId}-header` } : {})}
      className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200"
    >
      <span
        {...(automationId ? { id: `${automationId}-number`, "data-testid": `${automationId}-number`, "data-qa": `${automationId}-number` } : {})}
        className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0"
      >
        {num}
      </span>
      <div>
        <p
          {...(automationId ? { id: `${automationId}-title`, "data-testid": `${automationId}-title`, "data-qa": `${automationId}-title` } : {})}
          className="text-sm font-semibold text-gray-800"
        >
          {title}
        </p>
        {subtitle && (
          <p
            {...(automationId ? { id: `${automationId}-subtitle`, "data-testid": `${automationId}-subtitle`, "data-qa": `${automationId}-subtitle` } : {})}
            className="text-xs text-gray-500"
          >
            {subtitle}
          </p>
        )}
      </div>
    </div>
    <div
      {...(automationId ? { id: `${automationId}-body`, "data-testid": `${automationId}-body`, "data-qa": `${automationId}-body` } : {})}
      className="p-5"
    >
      {children}
    </div>
  </div>
);

// ─── StatusToggle ─────────────────────────────────────────────────────────────
const StatusToggle: React.FC<{ label: string; dotColor: string; value: boolean; onChange: (v: boolean) => void; automationId?: string }> = ({ label, dotColor, value, onChange, automationId }) => (
  <div
    {...(automationId ? { id: automationId, "data-testid": automationId, "data-qa": automationId } : {})}
    data-qa-value={value ? "active" : "inactive"}
    className="flex gap-2"
  >
    <button
      {...(automationId ? { id: `${automationId}-active`, "data-testid": `${automationId}-active`, "data-qa": `${automationId}-active` } : {})}
      data-qa-selected={value ? "true" : "false"}
      type="button"
      onClick={() => onChange(true)}
      className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${value ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>
      Active
    </button>
    <button
      {...(automationId ? { id: `${automationId}-inactive`, "data-testid": `${automationId}-inactive`, "data-qa": `${automationId}-inactive` } : {})}
      data-qa-selected={!value ? "true" : "false"}
      type="button"
      onClick={() => onChange(false)}
      className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${!value ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>
      Inactive
    </button>
  </div>
);

// ─── IcdSourcePicker (wide segmented control + content) ────────────────────────
const IcdSourcePicker: React.FC<{
  title: string;
  selected: string | null;
  onSelect: (key: string | null) => void;
  counts: { list_of_icd: number; medical_icd_groups: number; custom_icd_groups: number };
  automationId?: string;
  children: React.ReactNode;
}> = ({ title, selected, onSelect, counts, automationId, children }) => {
  const options = [
    { key: "list_of_icd", label: "List of ICD", icon: "fa-file-medical" },
    { key: "medical_icd_groups", label: "Medical Groups", icon: "fa-layer-group" },
    { key: "custom_icd_groups", label: "Custom Groups", icon: "fa-cubes" },
  ];
  return (
    <div {...(automationId ? { id: automationId, "data-testid": automationId, "data-qa": automationId } : {})}>
      <Label
        {...(automationId ? { id: `${automationId}-label`, "data-testid": `${automationId}-label`, "data-qa": `${automationId}-label` } : {})}
        className="mb-2 block"
      >
        {title}
      </Label>
      <div
        {...(automationId ? { id: `${automationId}-options`, "data-testid": `${automationId}-options`, "data-qa": `${automationId}-options` } : {})}
        className="flex bg-slate-100 rounded-lg p-1 border border-slate-200 gap-1"
      >
        {options.map((opt) => {
          const isSelected = selected === opt.key;
          const count = (counts as any)[opt.key];
          return (
            <button
              key={opt.key}
              {...(automationId ? { id: `${automationId}-option-${opt.key}`, "data-testid": `${automationId}-option-${opt.key}`, "data-qa": `${automationId}-option-${opt.key}` } : {})}
              data-qa-selected={isSelected ? "true" : "false"}
              data-qa-count={count}
              type="button"
              onClick={() => onSelect(isSelected ? null : opt.key)}
              className={`flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition-all ${isSelected ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              <i
                {...(automationId ? { id: `${automationId}-option-${opt.key}-icon`, "data-testid": `${automationId}-option-${opt.key}-icon`, "data-qa": `${automationId}-option-${opt.key}-icon` } : {})}
                className={`fa-solid ${opt.icon} text-xs`}
              ></i>
              <span
                {...(automationId ? { id: `${automationId}-option-${opt.key}-label`, "data-testid": `${automationId}-option-${opt.key}-label`, "data-qa": `${automationId}-option-${opt.key}-label` } : {})}
              >
                {opt.label}
              </span>
              {count > 0 && (
                <span
                  {...(automationId ? { id: `${automationId}-option-${opt.key}-count`, "data-testid": `${automationId}-option-${opt.key}-count`, "data-qa": `${automationId}-option-${opt.key}-count` } : {})}
                  className={`text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center ${isSelected ? "bg-blue-100 text-blue-700" : "bg-slate-300 text-slate-600"}`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {selected && (
        <div
          {...(automationId ? { id: `${automationId}-content`, "data-testid": `${automationId}-content`, "data-qa": `${automationId}-content` } : {})}
          className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4"
        >
          {children}
        </div>
      )}
    </div>
  );
};
// ─── Main Component ───────────────────────────────────────────────────────────
const IcdNotAllowed: React.FC = () => {
  const [items, setItems] = useState<icdNotAllowedIcd>({ result: [] });

  // ─── Role-based access ────────────────────────────────────────────────────
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ─── Force Dialog state ───────────────────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");

  const [mainDiagnoses, setMainDiagnoses] = useState<Option[]>([]);
  const [IcdDescription] = useState<string>("");
  const [notAllowedDiagnoses, setNotAllowedDiagnoses] = useState<Option[]>([]);
  const [relationName, setRelationName] = useState<string>("");
  const [mainCustomIcdGroups, setMainCustomIcdGroups] = useState<Option[]>([]);
  const [mainmedicalIcdGroups, setMainMedicalIcdGroups] = useState<Option[]>([]);
  const [notAllowedcustomIcdGroups, setNotAllowedCustomIcdGroups] = useState<Option[]>([]);
  const [notAllowedmedicalIcdGroups, setNotAllowedMedicalIcdGroups] = useState<Option[]>([]);

  const [provider, setProvider] = useState<Option[]>([]);
  const [providerMode, setProviderMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isProviderActive, setIsProviderActive] = useState(false);
  const [selectAllProviders, setSelectAllProviders] = useState(false);

  const [doctor, setDoctor] = useState<Option[]>([]);
  const [doctorMode, setDoctorMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isDoctorActive, setIsDoctorActive] = useState(false);
  const [selectAllDoctor, setSelectAllDoctor] = useState(false);

  const [specialty, setSpecialty] = useState<Option[]>([]);
  const [specialtyMode, setSpecialtyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isSpecialtyActive, setIsSpecialtyActive] = useState(false);
  const [selectAllSpecialty, setSelectAllSpecialty] = useState(false);
  const [clearSpecialty, setClearSpecialty] = useState(false);

  const [networks, setNetworks] = useState<Option[]>([]);
  const [networkMode, setNetworkMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isNetworkActive, setIsNetworkActive] = useState(false);
  const [selectAllNetworks, setSelectAllNetworks] = useState(false);

  const [insuranceplan, setInsuranceplan] = useState<Option[]>([]);
  const [insuranceMode, setInsuranceMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isInsuranceActive, setIsInsuranceActive] = useState(false);
  const [selectAllInsurance, setSelectAllInsurance] = useState(false);

  const [Payer, setPayer] = useState<string[]>([]);
  const [payerMode, setPayerMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPayerActive, setIsPayerActive] = useState(false);
  const [selectAllPayer, setSelectAllPayer] = useState(false);

  const [policy, setPolicy] = useState<PolicyItem[]>([]);
  const [policyMode, setPolicyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPolicyActive, setIsPolicyActive] = useState(false);
  const [selectAllPolicy, setSelectAllPolicy] = useState(false);

  const [userLevel, setUserLevel] = useState<string[]>([]);
  const [userMode, setUserMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isUserActive, setIsUserActive] = useState(false);
  const [selectAllUser, setSelectAllUser] = useState(false);

  const [patients, setPatients] = useState<Option[]>([]);
  const [patientMode, setPatientMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPatientActive, setIsPatientActive] = useState(false);
  const [selectAllPatients, setSelectAllPatients] = useState(false);

  const [vip, setVip] = useState<Option[]>([]);
  const [vipMode, setVipMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isVipActive, setIsVipActive] = useState(false);
  const [selectAllVip, setSelectAllVip] = useState(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [clearMainDiagnoses, setClearMainDiagnoses] = useState(false);
  const [clearNotAllowedDiagnoses, setClearNotAllowedDiagnoses] = useState(false);
  const [clearNetworks, setClearNetworks] = useState(false);
  const [clearInsuranceplan, setClearInsuranceplan] = useState(false);
  const [clearProvider, setClearProvider] = useState(false);
  const [clearPatient, setClearPatient] = useState(false);
  const [clearPolicy, setClearPolicy] = useState(false);
  const [clearMainCustomIcdGroups, setClearMainCustomIcdGroups] = useState(false);
  const [clearMainMedicalIcdGroups, setClearMainMedicalIcdGroups] = useState(false);
  const [clearNotAllowedCustomIcdGroups, setClearNotAllowedCustomIcdGroups] = useState(false);
  const [clearNotAllowedMedicalIcdGroups, setClearNotAllowedMedicalIcdGroups] = useState(false);
  const [clearDoctor, setClearDoctor] = useState(false);
  const [clearVip, setClearVip] = useState(false);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [allCodesDialogOpen, setAllCodesDialogOpen] = useState(false);
  const [allCodesDialogTitle, setAllCodesDialogTitle] = useState("");
  const [allCodesDialogList, setAllCodesDialogList] = useState<string[]>([]);
  const [showEntities, setShowEntities] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);
  const [selectedNotAllowedOption, setSelectedNotAllowedOption] = useState<string | null>(null);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  const [filterStatus, setFilterStatus] = useState<string>("");

  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterSearch, setFilterSearch] = useState<string>("");

  // ─── QA Automation: nested shared dropdowns/search/file upload ──────────────
  // Some controls are rendered inside shared components / react-select.
  // This attaches stable selectors to the actual inner input/select element.
  const applyNestedControlQaAttrs = () => {
    const setControlQaAttrs = (wrapperQaId: string, controlTestId: string) => {
      const wrapper = document.getElementById(wrapperQaId);
      const control = wrapper?.querySelector("input, select, textarea") as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement
        | null;

      if (control) {
        control.setAttribute("id", controlTestId);
        control.setAttribute("data-testid", controlTestId);
        control.setAttribute("data-qa", controlTestId);
      }
    };

    // Toolbar / Search
    setControlQaAttrs(qaId("wrapper-toolbar-search"), "icd-not-allowed-input-search");

    // Diagnosis One source dropdowns
    setControlQaAttrs(qaId("wrapper-diagnosis-one-list-of-icd"), "icd-not-allowed-select-diagnosis-one-icd");
    setControlQaAttrs(qaId("wrapper-diagnosis-one-medical-icd-groups"), "icd-not-allowed-select-diagnosis-one-medical-group");
    setControlQaAttrs(qaId("wrapper-diagnosis-one-custom-icd-groups"), "icd-not-allowed-select-diagnosis-one-custom-group");

    // Diagnosis Two source dropdowns
    setControlQaAttrs(qaId("wrapper-diagnosis-two-list-of-icd"), "icd-not-allowed-select-diagnosis-two-icd");
    setControlQaAttrs(qaId("wrapper-diagnosis-two-medical-icd-groups"), "icd-not-allowed-select-diagnosis-two-medical-group");
    setControlQaAttrs(qaId("wrapper-diagnosis-two-custom-icd-groups"), "icd-not-allowed-select-diagnosis-two-custom-group");

    // Entity dropdowns
    setControlQaAttrs(qaId("wrapper-provider-selector"), "icd-not-allowed-select-provider");
    setControlQaAttrs(qaId("wrapper-doctor-selector"), "icd-not-allowed-select-doctor");
    setControlQaAttrs(qaId("wrapper-specialty-selector"), "icd-not-allowed-select-specialty");
    setControlQaAttrs(qaId("wrapper-network-selector"), "icd-not-allowed-select-network");
    setControlQaAttrs(qaId("wrapper-insurance-plan-selector"), "icd-not-allowed-select-insurance-plan");
    setControlQaAttrs(qaId("wrapper-policy-selector"), "icd-not-allowed-select-policy");
    setControlQaAttrs(qaId("wrapper-patient-selector"), "icd-not-allowed-select-patient");
    setControlQaAttrs(qaId("wrapper-vip-selector"), "icd-not-allowed-select-vip");
  };

  useEffect(() => {
    const timer = window.setTimeout(applyNestedControlQaAttrs, 0);
    return () => window.clearTimeout(timer);
  }, [
    isDialogOpen,
    selectedMainOption,
    selectedNotAllowedOption,
    showEntities,
    filterSearch,
    clearMainDiagnoses,
    clearNotAllowedDiagnoses,
    clearMainCustomIcdGroups,
    clearMainMedicalIcdGroups,
    clearNotAllowedCustomIcdGroups,
    clearNotAllowedMedicalIcdGroups,
    clearNetworks,
    clearInsuranceplan,
    clearProvider,
    clearPatient,
    clearPolicy,
    clearDoctor,
    clearVip,
    clearSpecialty,
  ]);


  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };


  useEffect(() => { fetchUsers(); }, []);

  const resetAllEntityStates = () => {
    setProvider([]); setProviderMode(null); setIsProviderActive(false); setSelectAllProviders(false); setClearProvider(true);
    setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setSelectAllDoctor(false); setClearDoctor(true);
    setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setSelectAllSpecialty(false); setClearSpecialty(true);
    setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setSelectAllNetworks(false); setClearNetworks(true);
    setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setSelectAllInsurance(false); setClearInsuranceplan(true);
    setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false);
    setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setSelectAllPolicy(false); setClearPolicy(true);
    setUserLevel([]); setUserMode(null); setIsUserActive(false); setSelectAllUser(false);
    setPatients([]); setPatientMode(null); setIsPatientActive(false); setSelectAllPatients(false); setClearPatient(true);
    setVip([]); setVipMode(null); setIsVipActive(false); setSelectAllVip(false); setClearVip(true);
  };

  const validateEntities = (): string | null => {
    const checks = [
      { isActive: isProviderActive,   hasData: selectAllProviders  || provider.length > 0,     label: "Providers" },
      { isActive: isDoctorActive,     hasData: selectAllDoctor     || doctor.length > 0,        label: "Doctor" },
      { isActive: isSpecialtyActive,  hasData: selectAllSpecialty  || specialty.length > 0,     label: "Specialty" },
      { isActive: isNetworkActive,    hasData: selectAllNetworks   || networks.length > 0,      label: "Networks" },
      { isActive: isInsuranceActive,  hasData: selectAllInsurance  || insuranceplan.length > 0, label: "Insurance Plan" },
      { isActive: isPolicyActive,     hasData: selectAllPolicy     || policy.length > 0,        label: "Policy" },
      { isActive: isPatientActive,    hasData: selectAllPatients   || patients.length > 0,      label: "Patients" },
      { isActive: isVipActive,        hasData: selectAllVip        || vip.length > 0,           label: "VIP" },
      { isActive: isPayerActive,      hasData: selectAllPayer      || Payer.length > 0,         label: "Payer" },
      { isActive: isUserActive,       hasData: selectAllUser       || userLevel.length > 0,     label: "User Level" },
    ];
    const empty = checks.filter(c => c.isActive && !c.hasData).map(c => c.label);
    if (empty.length >= 1) {
      return `The following cards are open but have no data selected: ${empty.join(", ")}. Please select at least one item for each, or close them using the ✕ button.`;
    }
    return null;
  };

  const clear = () => {
    setRelationName("");
    setMainDiagnoses([]);
    setNotAllowedDiagnoses([]);
    setMainCustomIcdGroups([]);
    setMainMedicalIcdGroups([]);
    setNotAllowedCustomIcdGroups([]);
    setNotAllowedMedicalIcdGroups([]);
    setSelectedNotAllowedOption(null);
    setSelectedMainOption(null);
    setIsActive(true);
    setShowEntities(false);
    resetAllEntityStates();
  };

  const handleOpenDialog = (item: Datum) => { setSelectedItem(item); setIsViewDialogOpen(true); };
const handleCloseDialog = () => { setIsViewDialogOpen(false); setSelectedItem(null); setShowAllUpdates(false); };
  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };
const handleEditClick = (item: Datum) => {
  setClearProvider(false); setClearDoctor(false); setClearNetworks(false);
  setClearInsuranceplan(false); setClearPatient(false); setClearPolicy(false);
  setClearVip(false); setClearSpecialty(false);
  setClearMainDiagnoses(false); setClearNotAllowedDiagnoses(false);
  setClearMainCustomIcdGroups(false); setClearMainMedicalIcdGroups(false);
  setClearNotAllowedCustomIcdGroups(false); setClearNotAllowedMedicalIcdGroups(false);
  handleEdit(item);
  setIsDialogOpen(true);
};


  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Datum, { search: string; status: string; deleted: ServerDeletedFilterValue }>({
    filters: { search: filterSearch, status: filterStatus, deleted: deletedFilter },
    fetchPage: ({ page, limit, filters }) =>
      IcdNotAllowedIcdAxiosInstance.get(
        "getIcdNotAllowed",
        {
          params: {
            page,
            limit,
            name: filters.search.trim() || undefined,
            disease: filters.search.trim() || undefined,
            relation_name: filters.search.trim() || undefined,
            is_active: filters.status === "true" || filters.status === "false" ? filters.status : undefined,
            is_deleted: filters.deleted,
          },
        }
      ),
    setRows: (rows) => setItems({ result: rows }),
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    onError: (error: any) => {
      console.error("Failed to fetch ICD Not Allowed relations", error);
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch ICD Not Allowed relations";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };
  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  // ─── Soft Delete ──────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await IcdNotAllowedIcdAxiosInstance.delete(`softDeleteIcdNotAllowed/${id}`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "ICD Not Allowed Deleted Successfully." });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ─── Restore ──────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await IcdNotAllowedIcdAxiosInstance.patch(`restoreIcdNotAllowed/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "ICD Not Allowed restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore", variant: "destructive" });
    }
  };

  // ─── Hard Delete (permanent) ──────────────────────────────────────────────
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteIcdNotAllowed/${id}?force=true` : `deleteIcdNotAllowed/${id}`;
      const response = await IcdNotAllowedIcdAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "ICD Not Allowed Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete");
    }
  };

  // ─── Force Soft Delete ────────────────────────────────────────────────────
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await IcdNotAllowedIcdAxiosInstance.delete(`softDeleteIcdNotAllowed/${id}?force=true`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "ICD Not Allowed force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete");
    }
  };

  const buildPolicyData = (p: any) => {
    if (typeof p === "object" && p.Policy_id && !p.value) return { id: p.id || 0, Policy_id: p.Policy_id, Policy_name: p.Policy_name, plans: p.plans || [] };
    if (typeof p === "object" && p.value) return { id: p.value.id || 0, Policy_id: p.value.Policy_id || "", Policy_name: p.value.Policy_name || "", plans: p.value.plans || [] };
    if (typeof p === "string") return { id: 0, Policy_id: p, Policy_name: p, plans: [] };
    return { id: 0, Policy_id: "", Policy_name: "", plans: [] };
  };

  const buildProviderData = (o: any) => {
    if (o.providerId && o.providerName) return { Provider_id: o.providerId, Provider_name: o.providerName };
    const match = o.label?.match(/^\((.+)\) (.+)$/);
    if (match) return { Provider_id: match[1].trim(), Provider_name: match[2].trim() };
    return { Provider_id: o.value?.toString() || "", Provider_name: o.label?.toString() || "" };
  };

  const buildDoctorData = (o: any) => {
    if (o.doctorId && o.doctorName) return { Doctor_id: o.doctorId, Doctor_name: o.doctorName };
    const match = o.label?.match(/^\((.+)\) (.+)$/);
    if (match) return { Doctor_id: match[1].trim(), Doctor_name: match[2].trim() };
    return { Doctor_id: o.value?.toString() || "", Doctor_name: o.label?.toString() || "" };
  };

  const toggleActiveStatus = async (item: Datum) => {
    try {
      const updatedItem = {
        data: {
          id: item.id,
          relation_name: item.relation_name,
          is_active: !item.is_active,
          main_diagnoses: item.main_diagnoses,
          not_allowed_diagnoses: item.not_allowed_diagnoses,
          custom_groups: item.custom_groups?.map((g: any) => g.id ?? g),
          medical_groups: item.medical_groups?.map((g: any) => g.id ?? g),
          not_allowed_custom_groups: item.not_allowed_custom_groups?.map((g: any) => g.id ?? g),
          not_allowed_medical_groups: item.not_allowed_medical_groups?.map((g: any) => g.id ?? g),
          provider: (item.provider || []).map((g: any) => ({ Provider_id: g.Provider_id, Provider_name: g.Provider_name })),
          provider_mode: item.provider_mode ?? "DISABLED",
          provider_all: item.provider_all ?? false,
          networks: (item.network ?? item.networks ?? []).map((g: any) => g.id ?? g),
          network_mode: item.network_mode ?? "DISABLED",
          network_all: item.network_all ?? false,
          insuranceplan: (item.insuranceplan || []).map((g: any) => g.id ?? g),
          insuranceplan_mode: item.insuranceplan_mode ?? "DISABLED",
          insuranceplan_all: item.insuranceplan_all ?? false,
          payer: item.payer || [],
          payer_mode: item.payer_mode ?? "DISABLED",
          payer_all: item.payer_all ?? false,
          patients: (item.patient ?? item.patients ?? []).map((g: any) => ({ name: g.name ?? g })),
          patient_mode: item.patient_mode ?? "DISABLED",
          patient_all: item.patient_all ?? false,
          policy: (item.policy || []).map((g: any) => ({ id: g.id, Policy_id: g.Policy_id, Policy_name: g.Policy_name, plans: g.plans ?? [] })),
          policy_mode: item.policy_mode ?? "DISABLED",
          policy_all: item.policy_all ?? false,
          user_level: item.user_level || [],
          user_mode: item.user_mode ?? "DISABLED",
          user_all: item.user_all ?? false,
          vip: (item.vip || []).map((g: any) => g.id ?? g),
          vip_mode: item.vip_mode ?? "DISABLED",
          vip_all: item.vip_all ?? false,
          doctor: (item.doctor || []).map((g: any) => ({ Doctor_id: g.Doctor_id ?? "", Doctor_name: g.Doctor_name ?? g })),
          doctor_mode: item.doctor_mode ?? "DISABLED",
          doctor_all: item.doctor_all ?? false,
          specialty: (item.specialty || []).map((g: any) => g.id ?? g),
          specialty_mode: item.specialty_mode ?? "DISABLED",
          specialty_all: item.specialty_all ?? false,
        },
        token,
      };
const response = await IcdNotAllowedIcdAxiosInstance.put(`updateIcdNotAllowed`, updatedItem);
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: `Status changed to ${!item.is_active ? "Active" : "Inactive"}.` });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to update status.", variant: "destructive" });
    }
  };

  const addItem = async (): Promise<void> => {
    if (!relationName.trim()) { toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return; }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    try {
      const newGroupArray = [{
        relation_name: relationName,
        custom_groups: mainCustomIcdGroups.map((o) => o.value),
        medical_groups: mainmedicalIcdGroups.map((o) => o.value),
        main_diagnoses: mainDiagnoses.map((o) => o.value),
        not_allowed_diagnoses: notAllowedDiagnoses.map((o) => o.value),
        not_allowed_medical_groups: notAllowedmedicalIcdGroups.map((o) => o.value),
        not_allowed_custom_groups: notAllowedcustomIcdGroups.map((o) => o.value),
        provider: selectAllProviders ? [] : provider.map(buildProviderData),
        provider_mode: cardToMode(providerMode),
        provider_all: selectAllProviders,
        networks: selectAllNetworks ? [] : networks.map((o) => o.value),
        network_mode: cardToMode(networkMode),
        network_all: selectAllNetworks,
        insuranceplan: selectAllInsurance ? [] : insuranceplan.map((o) => Number(o.value)),
        insuranceplan_mode: cardToMode(insuranceMode),
        insuranceplan_all: selectAllInsurance,
        payer: selectAllPayer ? [] : Payer,
        payer_mode: cardToMode(payerMode),
        payer_all: selectAllPayer,
        patients: selectAllPatients ? [] : patients.map((o) => o.label),
        patient_mode: cardToMode(patientMode),
        patient_all: selectAllPatients,
        policy: selectAllPolicy ? [] : policy.map(buildPolicyData),
        policy_mode: cardToMode(policyMode),
        policy_all: selectAllPolicy,
        user_level: selectAllUser ? [] : userLevel,
        user_mode: cardToMode(userMode),
        user_all: selectAllUser,
        vip: selectAllVip ? [] : vip.map((o) => o.value),
        vip_mode: cardToMode(vipMode),
        vip_all: selectAllVip,
        doctor: selectAllDoctor ? [] : doctor.map(buildDoctorData),
        doctor_mode: cardToMode(doctorMode),
        doctor_all: selectAllDoctor,
        specialty: selectAllSpecialty ? [] : specialty.map((o) => o.value),
        specialty_mode: cardToMode(specialtyMode),
        specialty_all: selectAllSpecialty,
        is_active: isActive,
      }];
      const response = await IcdNotAllowedIcdAxiosInstance.post("addNewIcdNotAllowed", { data: newGroupArray, token });
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "New ICD Not Allowed Added Successfully." });
      } else {
        const errors = response.data.errors || [];
        toast({ title: "Error", description: errors.map((e: any) => e.error).join("\n") || response.data.message || "Failed to add the group.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Datum) => {
    const mapToOptions = (arr: (string | number)[]): Option[] => arr.map((value) => ({ value, label: value.toString() }));
    setRelationName(item.relation_name);
    setNotAllowedDiagnoses(mapToOptions(item.not_allowed_diagnoses));
    setMainDiagnoses(mapToOptions(item.main_diagnoses));

    const CustomIcdGroupsData = Array.isArray(item.custom_groups) ? item.custom_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setMainCustomIcdGroups(CustomIcdGroupsData.map((obj) => ({ value: obj.id, label: obj.name })));

    const MedicalIcdGroupsData = Array.isArray(item.medical_groups) ? item.medical_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setMainMedicalIcdGroups(MedicalIcdGroupsData.map((obj) => ({ value: obj.id, label: obj.name })));

    const NotAllowedCustomIcdGroupsData = Array.isArray(item.not_allowed_custom_groups) ? item.not_allowed_custom_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setNotAllowedCustomIcdGroups(NotAllowedCustomIcdGroupsData.map((obj) => ({ value: obj.id, label: obj.name })));

    const NotAllowedMedicalIcdGroupsData = Array.isArray(item.not_allowed_medical_groups) ? item.not_allowed_medical_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setNotAllowedMedicalIcdGroups(NotAllowedMedicalIcdGroupsData.map((obj) => ({ value: obj.id, label: obj.name })));

    const networksSource = item.network ?? item.networks ?? [];
    const networksData = Array.isArray(networksSource) ? networksSource.map((obj: any) => typeof obj === "string" ? { id: 0, network_name: obj } : obj) : [];
    const mappedNetworks = networksData.map((obj: any) => ({ value: obj.id, label: obj.id ? `(${obj.id}) ${obj.network_name}` : obj.network_name }));
    setNetworks(mappedNetworks);
    const nCard = modeToCard(item.network_mode);
    setNetworkMode(nCard.cardMode); setIsNetworkActive(nCard.isActive || mappedNetworks.length > 0);
    setSelectAllNetworks(item.network_all ?? false);

    const insuranceplanData = Array.isArray(item.insuranceplan) ? item.insuranceplan.map((obj) => typeof obj === "string" ? { id: 0, Plan_name: obj } : obj) : [];
    const mappedInsurance = insuranceplanData.map((obj) => ({ value: obj.id, label: obj.id ? `(${obj.id}) ${obj.Plan_name}` : obj.Plan_name }));
    setInsuranceplan(mappedInsurance);
    const iCard = modeToCard(item.insuranceplan_mode);
    setInsuranceMode(iCard.cardMode); setIsInsuranceActive(iCard.isActive || mappedInsurance.length > 0);
    setSelectAllInsurance(item.insuranceplan_all ?? false);

    const ProvidersData = Array.isArray(item.provider) ? item.provider.map((obj) => typeof obj === "string" ? { Provider_id: 0, Provider_name: obj } : obj) : [];
    const mappedProviders = ProvidersData.map((obj) => ({ value: obj.Provider_id, label: obj.Provider_id ? `(${obj.Provider_id}) ${obj.Provider_name}` : obj.Provider_name }));
    setProvider(mappedProviders);
    const pCard = modeToCard(item.provider_mode);
    setProviderMode(pCard.cardMode); setIsProviderActive(pCard.isActive || mappedProviders.length > 0);
    setSelectAllProviders(item.provider_all ?? false);

    const patientsSource = item.patient ?? item.patients ?? [];
    const PatientsData = Array.isArray(patientsSource) ? patientsSource.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    const mappedPatients = PatientsData.map((obj: any) => ({ value: obj.id, label: obj.id ? `(${obj.id}) ${obj.name}` : obj.name }));
    setPatients(mappedPatients);
    const patCard = modeToCard(item.patient_mode);
    setPatientMode(patCard.cardMode); setIsPatientActive(patCard.isActive || mappedPatients.length > 0);
    setSelectAllPatients(item.patient_all ?? false);

    const mappedPolicy = Array.isArray(item.policy) ? item.policy.map((obj) => ({ id: obj.id || 0, Policy_id: obj.Policy_id || "", Policy_name: obj.Policy_name || "", plans: obj.plans || [] })) : [];
    setPolicy(mappedPolicy);
    const polCard = modeToCard(item.policy_mode);
    setPolicyMode(polCard.cardMode); setIsPolicyActive(polCard.isActive || mappedPolicy.length > 0);
    setSelectAllPolicy(item.policy_all ?? false);

    const vipData = Array.isArray(item.vip) ? item.vip.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    const mappedVip = vipData.map((obj) => ({ value: obj.id, label: obj.id ? `(${obj.id}) ${obj.name}` : obj.name }));
    setVip(mappedVip);
    const vCard = modeToCard(item.vip_mode);
    setVipMode(vCard.cardMode); setIsVipActive(vCard.isActive || mappedVip.length > 0);
    setSelectAllVip(item.vip_all ?? false);

    const DoctorsData = Array.isArray(item.doctor) ? item.doctor.map((obj) => typeof obj === "string" ? { Doctor_id: 0, Doctor_name: obj } : obj) : [];
    const mappedDoctors = DoctorsData.map((obj) => ({ value: obj.Doctor_id, label: obj.Doctor_id ? `(${obj.Doctor_id}) ${obj.Doctor_name}` : obj.Doctor_name }));
    setDoctor(mappedDoctors);
    const dCard = modeToCard(item.doctor_mode);
    setDoctorMode(dCard.cardMode); setIsDoctorActive(dCard.isActive || mappedDoctors.length > 0);
    setSelectAllDoctor(item.doctor_all ?? false);

    const specialtyData = Array.isArray(item.specialty) ? item.specialty.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    const mappedSpecialty = specialtyData.map((obj: any) => ({ value: obj.id, label: obj.id ? `(${obj.id}) ${obj.name}` : obj.name }));
    setSpecialty(mappedSpecialty);
    const spCard = modeToCard(item.specialty_mode);
    setSpecialtyMode(spCard.cardMode); setIsSpecialtyActive(spCard.isActive || mappedSpecialty.length > 0);
    setSelectAllSpecialty(item.specialty_all ?? false);

    setPayer(item.payer || []);
    const payCard = modeToCard(item.payer_mode);
    setPayerMode(payCard.cardMode); setIsPayerActive(payCard.isActive || ((item.payer?.length ?? 0) > 0));
    setSelectAllPayer(item.payer_all ?? false);

    setUserLevel(item.user_level || []);
    const uCard = modeToCard(item.user_mode);
    setUserMode(uCard.cardMode); setIsUserActive(uCard.isActive || ((item.user_level?.length ?? 0) > 0));
    setSelectAllUser(item.user_all ?? false);

    setIsActive(item.is_active);
    setEditIndex(item.id);

    const hasEntities = !!(
      (item.provider?.length ?? 0) > 0 || (item.doctor?.length ?? 0) > 0 ||
      mappedSpecialty.length > 0 ||
      (networksSource as any[])?.length > 0 || (item.insuranceplan?.length ?? 0) > 0 ||
      (item.policy?.length ?? 0) > 0 || (patientsSource as any[])?.length > 0 ||
      (item.vip?.length ?? 0) > 0 || (item.payer?.length ?? 0) > 0 ||
      (item.user_level?.length ?? 0) > 0 ||
      (item.provider_mode      && item.provider_mode      !== "DISABLED") ||
      (item.network_mode       && item.network_mode       !== "DISABLED") ||
      (item.insuranceplan_mode && item.insuranceplan_mode !== "DISABLED") ||
      (item.payer_mode         && item.payer_mode         !== "DISABLED") ||
      (item.policy_mode        && item.policy_mode        !== "DISABLED") ||
      (item.patient_mode       && item.patient_mode       !== "DISABLED") ||
      (item.vip_mode           && item.vip_mode           !== "DISABLED") ||
      (item.doctor_mode        && item.doctor_mode        !== "DISABLED") ||
      (item.user_mode          && item.user_mode          !== "DISABLED") ||
      (item.specialty_mode     && item.specialty_mode     !== "DISABLED")
    );
    setShowEntities(hasEntities);

    if ((item.main_diagnoses?.length ?? 0) > 0) setSelectedMainOption("list_of_icd");
    else if ((item.medical_groups?.length ?? 0) > 0) setSelectedMainOption("medical_icd_groups");
    else if ((item.custom_groups?.length ?? 0) > 0) setSelectedMainOption("custom_icd_groups");
    else setSelectedMainOption(null);

    if ((item.not_allowed_diagnoses?.length ?? 0) > 0) setSelectedNotAllowedOption("list_of_icd");
    else if ((item.not_allowed_medical_groups?.length ?? 0) > 0) setSelectedNotAllowedOption("medical_icd_groups");
    else if ((item.not_allowed_custom_groups?.length ?? 0) > 0) setSelectedNotAllowedOption("custom_icd_groups");
    else setSelectedNotAllowedOption(null);
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!relationName.trim()) { toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return; }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    try {
      const updatedItem = {
        data: {
          relation_name: relationName,
          custom_groups: mainCustomIcdGroups.map((o) => o.value),
          medical_groups: mainmedicalIcdGroups.map((o) => o.value),
          main_diagnoses: mainDiagnoses.map((o) => o.value),
          not_allowed_diagnoses: notAllowedDiagnoses.map((o) => o.value),
          not_allowed_medical_groups: notAllowedmedicalIcdGroups.map((o) => o.value),
          not_allowed_custom_groups: notAllowedcustomIcdGroups.map((o) => o.value),
          provider: selectAllProviders ? [] : provider.map(buildProviderData),
          provider_mode: cardToMode(providerMode),
          provider_all: selectAllProviders,
          networks: selectAllNetworks ? [] : networks.map((o) => o.value),
          network_mode: cardToMode(networkMode),
          network_all: selectAllNetworks,
          insuranceplan: selectAllInsurance ? [] : insuranceplan.map((o) => Number(o.value)),
          insuranceplan_mode: cardToMode(insuranceMode),
          insuranceplan_all: selectAllInsurance,
          payer: selectAllPayer ? [] : Payer,
          payer_mode: cardToMode(payerMode),
          payer_all: selectAllPayer,
          patients: selectAllPatients ? [] : patients.map((o) => o.label),
          patient_mode: cardToMode(patientMode),
          patient_all: selectAllPatients,
          policy: selectAllPolicy ? [] : policy.map(buildPolicyData),
          policy_mode: cardToMode(policyMode),
          policy_all: selectAllPolicy,
          user_level: selectAllUser ? [] : userLevel,
          user_mode: cardToMode(userMode),
          user_all: selectAllUser,
          vip: selectAllVip ? [] : vip.map((o) => o.value),
          vip_mode: cardToMode(vipMode),
          vip_all: selectAllVip,
          doctor: selectAllDoctor ? [] : doctor.map(buildDoctorData),
          doctor_mode: cardToMode(doctorMode),
          doctor_all: selectAllDoctor,
          specialty: selectAllSpecialty ? [] : specialty.map((o) => o.value),
          specialty_mode: cardToMode(specialtyMode),
          specialty_all: selectAllSpecialty,
          is_active: isActive,
          id,
        },
        token,
      };
      const response = await IcdNotAllowedIcdAxiosInstance.put(`updateIcdNotAllowed`, updatedItem);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "ICD Not Allowed updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleDownload = (item: Datum) => {
    const rows = ["relation_name;main_diagnoses;medical_groups;custom_groups;not_allowed_diagnoses;not_allowed_medical_groups;not_allowed_custom_groups;provider;provider_mode;provider_all;network;network_mode;network_all;insuranceplan;insuranceplan_mode;insuranceplan_all;payer;payer_mode;payer_all;patient;patient_mode;patient_all;policy;policy_mode;policy_all;user_level;user_mode;user_all;doctor;doctor_mode;doctor_all;vip;vip_mode;vip_all;specialty;specialty_mode;specialty_all;is_active"];
    const icdCodes = item.main_diagnoses || []; const medicalGroups2 = item.medical_groups || []; const customGroups2 = item.custom_groups || [];
    const icdCodes2 = item.not_allowed_diagnoses || []; const medicalGroups = item.not_allowed_medical_groups || []; const customGroups = item.not_allowed_custom_groups || [];
    const providers = item.provider || [];
    const nets = item.network ?? item.networks ?? [];
    const insuranceplans = item.insuranceplan || [];
    const pats = item.patient ?? item.patients ?? [];
    const pols = item.policy || []; const userLevels = item.user_level || [];
    const docs = item.doctor || []; const payers = item.payer || []; const v = item.vip || [];
    const spec = (item as any).specialty || [];
    const maxLen = Math.max(icdCodes.length, medicalGroups2.length, customGroups2.length, icdCodes2.length, medicalGroups.length, customGroups.length, providers.length, nets.length, insuranceplans.length, pats.length, pols.length, userLevels.length, docs.length, payers.length, v.length, spec.length, 1);
    for (let index = 0; index < maxLen; index++) {
      rows.push([
        item.relation_name || "",
        index < icdCodes.length ? icdCodes[index] : "",
        index < medicalGroups2.length ? medicalGroups2[index].name || "" : "",
        index < customGroups2.length ? customGroups2[index].name || "" : "",
        index < icdCodes2.length ? icdCodes2[index] : "",
        index < medicalGroups.length ? medicalGroups[index].name || "" : "",
        index < customGroups.length ? customGroups[index].name || "" : "",
        index < providers.length ? providers[index].Provider_name || "" : "",
        item.provider_mode ?? "DISABLED", item.provider_all ? "true" : "false",
        index < nets.length ? nets[index].network_name || "" : "",
        item.network_mode ?? "DISABLED", item.network_all ? "true" : "false",
        index < insuranceplans.length ? insuranceplans[index].Plan_name || "" : "",
        item.insuranceplan_mode ?? "DISABLED", item.insuranceplan_all ? "true" : "false",
        index < payers.length ? payers[index] : "",
        item.payer_mode ?? "DISABLED", item.payer_all ? "true" : "false",
        index < pats.length ? pats[index].name || "" : "",
        item.patient_mode ?? "DISABLED", item.patient_all ? "true" : "false",
        index < pols.length ? pols[index].Policy_name || "" : "",
        item.policy_mode ?? "DISABLED", item.policy_all ? "true" : "false",
        index < userLevels.length ? userLevels[index] : "",
        item.user_mode ?? "DISABLED", item.user_all ? "true" : "false",
        index < docs.length ? docs[index].Doctor_name || "" : "",
        item.doctor_mode ?? "DISABLED", item.doctor_all ? "true" : "false",
        index < v.length ? v[index].name || "" : "",
        item.vip_mode ?? "DISABLED", item.vip_all ? "true" : "false",
        index < spec.length ? spec[index].name || "" : "",
        item.specialty_mode ?? "DISABLED", item.specialty_all ? "true" : "false",
        item.is_active ? "TRUE" : "FALSE",
      ].join(";"));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `IcdnotAllowed${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-id")} variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
     cell: ({ row }) => (
  <div
    id={rowQaId("cell-id", row.original.id)}
    data-testid={rowQaId("cell-id", row.original.id)}
    data-qa={rowQaId("cell-id", row.original.id)}
    className="w-full flex justify-center font-medium"
  >
    {row.index + 1}
  </div>
),
    },
    {
      accessorKey: "relation_name",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-relation-name")} variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Relation Name {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const val = row.getValue("relation_name") as string;
        const isDeleted = (row.original as any).is_deleted;
        return (
          <div
            id={rowQaId("cell-relation-name", row.original.id)}
            data-testid={rowQaId("cell-relation-name", row.original.id)}
            data-qa={rowQaId("cell-relation-name", row.original.id)}
            data-qa-deleted={isDeleted ? "true" : "false"}
            className={`w-full text-center ${isDeleted ? "line-through text-gray-400" : ""}`}
          >
            {Array.isArray(val) ? val.join(", ") : val}
          </div>
        );
      },
    },
    {
      accessorKey: "main_diagnoses",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-diagnosis-one")} variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Diagnosis One {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;

        const allValues = [
          ...(item.main_diagnoses || []).map((value: any) => ({
            label: String(value),
            type: "ICD",
          })),
          ...(item.custom_groups || []).map((group: any) => ({
            label: String(group.name || group),
            type: "Custom",
          })),
          ...(item.medical_groups || []).map((group: any) => ({
            label: String(group.name || group),
            type: "Medical",
          })),
        ];

        if (allValues.length === 0) {
          return (
            <div
              id={rowQaId("cell-diagnosis-one-empty", item.id)}
              data-testid={rowQaId("cell-diagnosis-one-empty", item.id)}
              data-qa={rowQaId("cell-diagnosis-one-empty", item.id)}
              className="w-full text-center"
            >
              -
            </div>
          );
        }

        const searchTerm = filterSearch.toLowerCase().trim();
        const hasSearch = searchTerm.length >= 2;

        const matchingValues = hasSearch
          ? allValues.filter((entry) =>
              entry.label.toLowerCase().includes(searchTerm),
            )
          : [];

        const hasMatchingValues = hasSearch && matchingValues.length > 0;

        const displayedValues = hasMatchingValues
          ? matchingValues.slice(0, 3)
          : allValues.slice(0, 3);

        const dialogValues = hasMatchingValues ? matchingValues : allValues;
        const remainingCount = Math.max(
          0,
          dialogValues.length - displayedValues.length,
        );

        const getStyle = (type: string, isMatching: boolean) => {
          if (isMatching) {
            return {
              bg: "#fef08a",
              border: "#facc15",
              color: "#713f12",
            };
          }

          if (type === "Medical") {
            return {
              bg: "#f0fdf4",
              border: "#86efac",
              color: "#166534",
            };
          }

          if (type === "Custom") {
            return {
              bg: "#faf5ff",
              border: "#d8b4fe",
              color: "#6b21a8",
            };
          }

          return {
            bg: "#eff6ff",
            border: "#bfdbfe",
            color: "#1d4ed8",
          };
        };

        return (
          <div
            id={rowQaId("cell-diagnosis-one", item.id)}
            data-testid={rowQaId("cell-diagnosis-one", item.id)}
            data-qa={rowQaId("cell-diagnosis-one", item.id)}
            className="w-full text-center px-2 py-1"
          >
            <div
              id={rowQaId("diagnosis-one-chip-list", item.id)}
              data-testid={rowQaId("diagnosis-one-chip-list", item.id)}
              data-qa={rowQaId("diagnosis-one-chip-list", item.id)}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                justifyContent: "center",
                marginBottom: 4,
              }}
            >
              {displayedValues.map(({ label, type }, index) => {
                const isMatching =
                  hasSearch &&
                  label.toLowerCase().includes(searchTerm);
                const style = getStyle(type, isMatching);

                return (
                  <div
                    key={`${type}-${label}-${index}`}
                    id={rowQaId(`diagnosis-one-chip-${index}`, item.id)}
                    data-testid={rowQaId(`diagnosis-one-chip-${index}`, item.id)}
                    data-qa={rowQaId(`diagnosis-one-chip-${index}`, item.id)}
                    data-qa-label={label}
                    data-qa-type={type}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 8px",
                      borderRadius: 6,
                      border: `1px solid ${style.border}`,
                      background: style.bg,
                    }}
                  >
                    <span
                      id={rowQaId(`diagnosis-one-chip-label-${index}`, item.id)}
                      data-testid={rowQaId(`diagnosis-one-chip-label-${index}`, item.id)}
                      data-qa={rowQaId(`diagnosis-one-chip-label-${index}`, item.id)}
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: style.color,
                      }}
                    >
                      {label}
                    </span>
                    <span
                      id={rowQaId(`diagnosis-one-chip-type-${index}`, item.id)}
                      data-testid={rowQaId(`diagnosis-one-chip-type-${index}`, item.id)}
                      data-qa={rowQaId(`diagnosis-one-chip-type-${index}`, item.id)}
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: style.color,
                        opacity: 0.7,
                        textTransform: "uppercase",
                      }}
                    >
                      {type}
                    </span>
                  </div>
                );
              })}
            </div>

            {remainingCount > 0 && (
              <button
                id={rowQaId("btn-show-more-diagnosis-one", item.id)}
                data-testid={rowQaId("btn-show-more-diagnosis-one", item.id)}
                data-qa={rowQaId("btn-show-more-diagnosis-one", item.id)}
                type="button"
                onClick={() => {
                  setAllCodesDialogList(
                    dialogValues.map(
                      ({ label, type }) => `[${type}] ${label}`,
                    ),
                  );
                  setAllCodesDialogTitle("Diagnosis One Codes");
                  setAllCodesDialogOpen(true);
                }}
                className={`text-xs font-semibold ${
                  hasMatchingValues
                    ? "text-yellow-600 hover:text-yellow-800"
                    : "text-blue-600 hover:text-blue-800"
                }`}
              >
                +{remainingCount} More
              </button>
            )}

            <div
              id={rowQaId("diagnosis-one-total-count", item.id)}
              data-testid={rowQaId("diagnosis-one-total-count", item.id)}
              data-qa={rowQaId("diagnosis-one-total-count", item.id)}
              style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}
            >
              {hasMatchingValues
                ? `${matchingValues.length}/${allValues.length}`
                : `${allValues.length} total`}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "not_allowed_diagnoses",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-diagnosis-two")} variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Diagnosis Two {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;

        const allValues = [
          ...(item.not_allowed_diagnoses || []).map((value: any) => ({
            label: String(value),
            type: "ICD",
          })),
          ...(item.not_allowed_custom_groups || []).map((group: any) => ({
            label: String(group.name || group),
            type: "Custom",
          })),
          ...(item.not_allowed_medical_groups || []).map((group: any) => ({
            label: String(group.name || group),
            type: "Medical",
          })),
        ];

        if (allValues.length === 0) {
          return (
            <div
              id={rowQaId("cell-diagnosis-two-empty", item.id)}
              data-testid={rowQaId("cell-diagnosis-two-empty", item.id)}
              data-qa={rowQaId("cell-diagnosis-two-empty", item.id)}
              className="w-full text-center"
            >
              -
            </div>
          );
        }

        const searchTerm = filterSearch.toLowerCase().trim();
        const hasSearch = searchTerm.length >= 2;

        const matchingValues = hasSearch
          ? allValues.filter((entry) =>
              entry.label.toLowerCase().includes(searchTerm),
            )
          : [];

        const hasMatchingValues = hasSearch && matchingValues.length > 0;

        const displayedValues = hasMatchingValues
          ? matchingValues.slice(0, 3)
          : allValues.slice(0, 3);

        const dialogValues = hasMatchingValues ? matchingValues : allValues;
        const remainingCount = Math.max(
          0,
          dialogValues.length - displayedValues.length,
        );

        const getStyle = (type: string, isMatching: boolean) => {
          if (isMatching) {
            return {
              bg: "#fef08a",
              border: "#facc15",
              color: "#713f12",
            };
          }

          if (type === "Medical") {
            return {
              bg: "#f0fdf4",
              border: "#86efac",
              color: "#166534",
            };
          }

          if (type === "Custom") {
            return {
              bg: "#faf5ff",
              border: "#d8b4fe",
              color: "#6b21a8",
            };
          }

          return {
            bg: "#eff6ff",
            border: "#bfdbfe",
            color: "#1d4ed8",
          };
        };

        return (
          <div
            id={rowQaId("cell-diagnosis-two", item.id)}
            data-testid={rowQaId("cell-diagnosis-two", item.id)}
            data-qa={rowQaId("cell-diagnosis-two", item.id)}
            className="w-full text-center px-2 py-1"
          >
            <div
              id={rowQaId("diagnosis-two-chip-list", item.id)}
              data-testid={rowQaId("diagnosis-two-chip-list", item.id)}
              data-qa={rowQaId("diagnosis-two-chip-list", item.id)}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                justifyContent: "center",
                marginBottom: 4,
              }}
            >
              {displayedValues.map(({ label, type }, index) => {
                const isMatching =
                  hasSearch &&
                  label.toLowerCase().includes(searchTerm);
                const style = getStyle(type, isMatching);

                return (
                  <div
                    key={`${type}-${label}-${index}`}
                    id={rowQaId(`diagnosis-two-chip-${index}`, item.id)}
                    data-testid={rowQaId(`diagnosis-two-chip-${index}`, item.id)}
                    data-qa={rowQaId(`diagnosis-two-chip-${index}`, item.id)}
                    data-qa-label={label}
                    data-qa-type={type}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 8px",
                      borderRadius: 6,
                      border: `1px solid ${style.border}`,
                      background: style.bg,
                    }}
                  >
                    <span
                      id={rowQaId(`diagnosis-two-chip-label-${index}`, item.id)}
                      data-testid={rowQaId(`diagnosis-two-chip-label-${index}`, item.id)}
                      data-qa={rowQaId(`diagnosis-two-chip-label-${index}`, item.id)}
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: style.color,
                      }}
                    >
                      {label}
                    </span>
                    <span
                      id={rowQaId(`diagnosis-two-chip-type-${index}`, item.id)}
                      data-testid={rowQaId(`diagnosis-two-chip-type-${index}`, item.id)}
                      data-qa={rowQaId(`diagnosis-two-chip-type-${index}`, item.id)}
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: style.color,
                        opacity: 0.7,
                        textTransform: "uppercase",
                      }}
                    >
                      {type}
                    </span>
                  </div>
                );
              })}
            </div>

            {remainingCount > 0 && (
              <button
                id={rowQaId("btn-show-more-diagnosis-two", item.id)}
                data-testid={rowQaId("btn-show-more-diagnosis-two", item.id)}
                data-qa={rowQaId("btn-show-more-diagnosis-two", item.id)}
                type="button"
                onClick={() => {
                  setAllCodesDialogList(
                    dialogValues.map(
                      ({ label, type }) => `[${type}] ${label}`,
                    ),
                  );
                  setAllCodesDialogTitle("Diagnosis Two Codes");
                  setAllCodesDialogOpen(true);
                }}
                className={`text-xs font-semibold ${
                  hasMatchingValues
                    ? "text-yellow-600 hover:text-yellow-800"
                    : "text-blue-600 hover:text-blue-800"
                }`}
              >
                +{remainingCount} More
              </button>
            )}

            <div
              id={rowQaId("diagnosis-two-total-count", item.id)}
              data-testid={rowQaId("diagnosis-two-total-count", item.id)}
              data-qa={rowQaId("diagnosis-two-total-count", item.id)}
              style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}
            >
              {hasMatchingValues
                ? `${matchingValues.length}/${allValues.length}`
                : `${allValues.length} total`}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active",
      header: () => <div {...qaAttrs("column-status-header")} className="w-full text-center font-normal">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex justify-center">
            <button
              id={rowQaId("toggle-status", item.id)}
              data-testid={rowQaId("toggle-status", item.id)}
              data-qa={rowQaId("toggle-status", item.id)}
              data-qa-status={item.is_active ? "active" : "inactive"}
              data-qa-deleted={isDeleted ? "true" : "false"}
              type="button"
              onClick={() => !isDeleted && toggleActiveStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active ? "bg-green-500" : "bg-gray-300"}`}
            >
              <span
                id={rowQaId("toggle-status-knob", item.id)}
                data-testid={rowQaId("toggle-status-knob", item.id)}
                data-qa={rowQaId("toggle-status-knob", item.id)}
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`}
              />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => <div {...qaAttrs("column-view-header")} className="w-full text-center font-normal">View</div>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button
            id={rowQaId("btn-view-details", row.original.id)}
            data-testid={rowQaId("btn-view-details", row.original.id)}
            data-qa={rowQaId("btn-view-details", row.original.id)}
            type="button"
            onClick={() => handleOpenDialog(row.original)}
            className="text-blue-600 hover:text-blue-800"
            title="View Details"
            aria-label={`View ICD Not Allowed details ${row.original.id}`}
          >
            <i
              id={rowQaId("icon-view-details", row.original.id)}
              data-testid={rowQaId("icon-view-details", row.original.id)}
              data-qa={rowQaId("icon-view-details", row.original.id)}
              className="fa-solid fa-eye text-lg"
            ></i>
          </button>
        </div>
      ),
    },
  ];
  return (
    <div
      {...qaAttrs("page")}
      className="h5-container mx-auto px-1 pt-16 pb-4"
      data-page-name="icd-not-allowed"
    >
      <div {...qaAttrs("page-header")} className="flex justify-between items-center">
        <h5 {...qaAttrs("page-title")} className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span {...qaAttrs("page-title-icon-wrapper")} className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i {...qaAttrs("page-title-icon")} className="fa-solid fa-times-circle"></i>
          </span>
          ICD Not Allowed
        </h5>
      </div>

      <div
        {...qaAttrs("data-table-wrapper")}
        data-delete-action-testid={qaId("data-table-action-delete")}
        data-delete-icon-testid={qaId("data-table-action-delete-icon")}
        data-restore-action-testid={qaId("data-table-action-restore")}
        data-hard-delete-action-testid={qaId("data-table-action-hard-delete")}
      >
      <DataTable2
        qaIdPrefix={qaId("data-table")}
        columns={columns}
        data={items.result}
        searchColumns={[]}
        addButtonLabel="Add New Relation"
        showAddButton={false}
        onEdit={handleEdit}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        validateButtonLabel="Validate"
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div {...qaAttrs("toolbar-filters")} style={{ width: "100%", position: "relative" }}>
<div {...qaAttrs("toolbar-row")} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 160px 10px 16px", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12 }}>
  <div {...qaAttrs("deleted-filter-wrapper")}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
  </div>
              <button {...qaAttrs("btn-toolbar-add-new-relation")} type="button" onClick={handleAddClick} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg {...qaAttrs("btn-toolbar-add-new-relation-icon")} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                <span {...qaAttrs("btn-toolbar-add-new-relation-label")}>Add New Relation</span>
              </button>
              <div {...qaAttrs("wrapper-toolbar-search")}>
                <UnifiedSearchBox
                  value={filterSearch}
                  onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                  helpText="Relation Name, ICD, Medical Group, or Custom Group on either diagnosis side."
                />
              </div>
              <div {...qaAttrs("toolbar-separator")} style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              <div {...qaAttrs("status-filter-section")} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span {...qaAttrs("status-filter-label")} style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>Status</span>
                <div {...qaAttrs("status-filter-wrapper")} style={{ display: "flex", alignItems: "center", padding: 4, background: "#e2e8f0", borderRadius: 9999 }}>
                  {[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }].map((option) => (
                    <button
                      key={option.value}
                      id={qaId(`btn-filter-status-${option.value || "all"}`)}
                      data-testid={qaId(`btn-filter-status-${option.value || "all"}`)}
                      data-qa={qaId(`btn-filter-status-${option.value || "all"}`)}
                      data-qa-selected={filterStatus === option.value ? "true" : "false"}
                      type="button"
                      onClick={() => setFilterStatus(option.value)}
                      style={{ padding: "6px 14px", fontSize: 13, fontWeight: 500, borderRadius: 9999, border: "none", cursor: "pointer", background: filterStatus === option.value ? option.value === "true" ? "#10b981" : option.value === "false" ? "#94a3b8" : "#ffffff" : "transparent", color: filterStatus === option.value ? option.value === "" ? "#334155" : "#ffffff" : "#64748b", boxShadow: filterStatus === option.value ? "0 2px 4px rgba(0,0,0,0.1)" : "none" }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div {...qaAttrs("wrapper-file-upload")} style={{ position: "absolute", top: "50%", right: 16, transform: "translateY(-50%)" }}>
                <FileUpload fetchItems={fetchItems} />
              </div>
            </div>
          </div>
        }
      />
      </div>

      {/* ===== VIEW DIALOG ===== */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent {...qaAttrs("dialog-view-details")} className="max-w-[900px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (
            <>
              <div {...qaAttrs("view-details-header")} style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div {...qaAttrs("view-details-main-icon-wrapper")} style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    <i {...qaAttrs("view-details-main-icon")} className="fa-solid fa-times-circle" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <h2 {...qaAttrs("view-details-relation-name")} style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selectedItem.relation_name}</h2>
                      <span {...qaAttrs("view-details-status-badge")} data-qa-status={selectedItem.is_active ? "active" : "inactive"} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.is_active ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.is_active ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                        <span {...qaAttrs("view-details-status-dot")} style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                        <span {...qaAttrs("view-details-status-label")}>{selectedItem.is_active ? "Active" : "Inactive"}</span>
                      </span>
                      {/* ✅ Deleted badge */}
                      {(selectedItem as any).is_deleted && (
                        <span {...qaAttrs("view-details-deleted-badge")} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                          <i {...qaAttrs("view-details-deleted-icon")} className="fa-solid fa-trash" style={{ fontSize: 10 }} /> <span {...qaAttrs("view-details-deleted-label")}>Deleted</span>
                        </span>
                      )}
                    </div>
                    <p {...qaAttrs("view-details-rule-id")} style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Rule ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
                  </div>
                </div>
              </div>

              <ScrollArea {...qaAttrs("view-details-scroll-area")} className="flex-1" style={{ background: "#f8fafc" }}>
                <div style={{ padding: "6px 0 16px" }}>
                  {/* Diagnosis One */}
                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                        <i className="fa-solid fa-stethoscope" style={{ fontSize: 12, color: "#3b82f6" }} />
                      </div>
                      <span {...qaAttrs("view-details-diagnosis-one-title")} style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Diagnosis One</span>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                        <span style={{ width: 6, height: 6, borderRadius: 2, background: "#3b82f6", flexShrink: 0 }} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>ICD Codes</span>
                        <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: "auto" }}>{(selectedItem.main_diagnoses || []).length} items</span>
                      </div>
                      <TagsWrap automationId={qaId("view-details-diagnosis-one-icd-tags")} items={selectedItem.main_diagnoses || []} color="blue" />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                          <span style={{ width: 6, height: 6, borderRadius: 2, background: "#22c55e", flexShrink: 0 }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Medical ICD Groups</span>
                        </div>
                        <TagsWrap automationId={qaId("view-details-diagnosis-one-medical-groups-tags")} items={(selectedItem.medical_groups || []).map((g: any) => g.name || g)} color="green" />
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                          <span style={{ width: 6, height: 6, borderRadius: 2, background: "#a855f7", flexShrink: 0 }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Custom ICD Groups</span>
                        </div>
                        <TagsWrap automationId={qaId("view-details-diagnosis-one-custom-groups-tags")} items={(selectedItem.custom_groups || []).map((g: any) => g.name || g)} color="purple" />
                      </div>
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  {/* Diagnosis Two */}
                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fecaca" }}>
                        <i className="fa-solid fa-ban" style={{ fontSize: 12, color: "#ef4444" }} />
                      </div>
                      <span {...qaAttrs("view-details-diagnosis-two-title")} style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Diagnosis Two</span>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                        <span style={{ width: 6, height: 6, borderRadius: 2, background: "#ef4444", flexShrink: 0 }} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>ICD Codes</span>
                        <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: "auto" }}>{(selectedItem.not_allowed_diagnoses || []).length} items</span>
                      </div>
                      <TagsWrap automationId={qaId("view-details-diagnosis-two-icd-tags")} items={selectedItem.not_allowed_diagnoses || []} color="gray" />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                          <span style={{ width: 6, height: 6, borderRadius: 2, background: "#22c55e", flexShrink: 0 }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Medical ICD Groups</span>
                        </div>
                        <TagsWrap automationId={qaId("view-details-diagnosis-two-medical-groups-tags")} items={(selectedItem.not_allowed_medical_groups || []).map((g: any) => g.name || g)} color="green" />
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                          <span style={{ width: 6, height: 6, borderRadius: 2, background: "#a855f7", flexShrink: 0 }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Custom ICD Groups</span>
                        </div>
                        <TagsWrap automationId={qaId("view-details-diagnosis-two-custom-groups-tags")} items={(selectedItem.not_allowed_custom_groups || []).map((g: any) => g.name || g)} color="purple" />
                      </div>
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  {/* Target Scope */}
                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fde68a" }}>
                        <i className="fa-solid fa-bullseye" style={{ fontSize: 12, color: "#d97706" }} />
                      </div>
                      <span {...qaAttrs("view-details-target-scope-title")} style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Target Scope & Visibility</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      {[
                        { label: "Providers",       icon: "fa-hospital",         color: "#3b82f6", items: (selectedItem.provider || []).map((p: any) => p.Provider_id ? `(${p.Provider_id}) ${p.Provider_name}` : p.Provider_name || String(p)),                                                                    mode: selectedItem.provider_mode,      all: (selectedItem as any).provider_all },
                        { label: "Doctors",         icon: "fa-stethoscope",      color: "#06b6d4", items: (selectedItem.doctor || []).map((d: any) => d.Doctor_id ? `(${d.Doctor_id}) ${d.Doctor_name}` : d.Doctor_name || String(d)),                                                                              mode: selectedItem.doctor_mode,        all: (selectedItem as any).doctor_all },
                        { label: "Specialty",       icon: "fa-user-doctor",      color: "#0ea5e9", items: ((selectedItem as any).specialty || []).map((s: any) => s.id ? `(${s.id}) ${s.name}` : s.name || String(s)),                                                                                              mode: selectedItem.specialty_mode,     all: (selectedItem as any).specialty_all },
                        { label: "Networks",        icon: "fa-network-wired",    color: "#8b5cf6", items: (selectedItem.network ?? selectedItem.networks ?? []).map((n: any) => n.id ? `(${n.id}) ${n.network_name}` : n.network_name || String(n)),                                                                mode: selectedItem.network_mode,       all: selectedItem.network_all },
                        { label: "Insurance Plans", icon: "fa-shield-halved",    color: "#f59e0b", items: (selectedItem.insuranceplan || []).map((i: any) => i.id ? `(${i.id}) ${i.Plan_name}` : i.Plan_name || String(i)),                                                                                        mode: selectedItem.insuranceplan_mode, all: (selectedItem as any).insuranceplan_all },
                        { label: "Policies",        icon: "fa-file-lines",       color: "#10b981", items: (selectedItem.policy || []).map((p: any) => `${p.Policy_id || ""} - ${p.Policy_name || ""}`),                                                                                                            mode: selectedItem.policy_mode,        all: (selectedItem as any).policy_all },
                        { label: "Patients",        icon: "fa-users",            color: "#ec4899", items: (selectedItem.patient ?? selectedItem.patients ?? []).map((p: any) => p.id ? `(${p.id}) ${p.name}` : p.name || String(p)),                                                                               mode: selectedItem.patient_mode,       all: selectedItem.patient_all },
                        { label: "VIP",             icon: "fa-star",             color: "#eab308", items: (selectedItem.vip || []).map((v: any) => v.id ? `(${v.id}) ${v.name}` : v.name || String(v)),                                                                                                            mode: selectedItem.vip_mode,           all: (selectedItem as any).vip_all },
                        { label: "Payer",           icon: "fa-building-columns", color: "#6366f1", items: (selectedItem.payer || []).map((p: any) => String(p)),                                                                                                                                                    mode: selectedItem.payer_mode,         all: (selectedItem as any).payer_all },
                        { label: "User Levels",     icon: "fa-user-gear",        color: "#64748b", items: (selectedItem.user_level || []).map((u: any) => String(u)),                                                                                                                                               mode: selectedItem.user_mode,          all: (selectedItem as any).user_all },
                      ].map((e) => {
                        const isExclude  = e.mode === "EXCLUDED";
                        const isDisabled = !e.mode || e.mode === "DISABLED";
                        return (
                          <div
                            key={e.label}
                            id={qaId(`view-details-entity-card-${qaSafe(e.label)}`)}
                            data-testid={qaId(`view-details-entity-card-${qaSafe(e.label)}`)}
                            data-qa={qaId(`view-details-entity-card-${qaSafe(e.label)}`)}
                            data-qa-mode={String(e.mode ?? "DISABLED")}
                            data-qa-all={e.all ? "true" : "false"}
                            style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", display: "flex", flexDirection: "column" }}
                          >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                <div style={{ width: 24, height: 24, borderRadius: 6, background: `${e.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <i className={`fa-solid ${e.icon}`} style={{ fontSize: 10, color: e.color }} />
                                </div>
                                <span id={qaId(`view-details-entity-card-${qaSafe(e.label)}-label`)} data-testid={qaId(`view-details-entity-card-${qaSafe(e.label)}-label`)} data-qa={qaId(`view-details-entity-card-${qaSafe(e.label)}-label`)} style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{e.label}</span>
                              </div>
                              {!isDisabled && (
                                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 9999, fontWeight: 600, background: isExclude ? "#fef2f2" : "#f0fdf4", color: isExclude ? "#dc2626" : "#15803d", border: `1px solid ${isExclude ? "#fecaca" : "#bbf7d0"}` }}>
                                  {isExclude ? "Exclusion" : "Inclusion"}
                                </span>
                              )}
                            </div>
                            {e.all
                              ? <span style={{ fontSize: 11, color: "#475569", fontStyle: "italic" }}>All {e.label}</span>
                              : e.items.length > 0
                                ? (
                                  <div id={qaId(`view-details-entity-card-${qaSafe(e.label)}-items`)} data-testid={qaId(`view-details-entity-card-${qaSafe(e.label)}-items`)} data-qa={qaId(`view-details-entity-card-${qaSafe(e.label)}-items`)} style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                                    {e.items.map((it: string, i: number) => (
                                      <span
                                        key={i}
                                        id={qaId(`view-details-entity-card-${qaSafe(e.label)}-item-${i}`)}
                                        data-testid={qaId(`view-details-entity-card-${qaSafe(e.label)}-item-${i}`)}
                                        data-qa={qaId(`view-details-entity-card-${qaSafe(e.label)}-item-${i}`)}
                                        data-qa-label={it}
                                        style={{ fontSize: 11, padding: "3px 10px", borderRadius: 9999, background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0", fontWeight: 500 }}
                                      >
                                        {it}
                                      </span>
                                    ))}
                                  </div>
                                )
                                : <span style={{ color: "#cbd5e1", fontSize: 12, fontStyle: "italic" }}>No items</span>
                            }
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  {/* Audit Trail */}
                  <div style={{ padding: "20px 28px 8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                        <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                      </div>
                      <span {...qaAttrs("view-details-audit-trail-title")} style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created By</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                            {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                        </div>
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created On</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                          <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{formatDate((selectedItem as any).createdat)}</span>
                        </div>
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last Updated</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                          <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{formatDate((selectedItem as any).updatedat)}</span>
                        </div>
                      </div>
                    </div>
{Array.isArray((selectedItem as any).updated_by) && (selectedItem as any).updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {(selectedItem as any).updated_by.length} update{(selectedItem as any).updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {((selectedItem as any).updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
            {i === 0 && (<span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>)}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{formatDate(entry.at)}</span>
        </div>
      ))}
      {(selectedItem as any).updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button
            {...qaAttrs("btn-toggle-update-history")}
            type="button"
            onClick={() => setShowAllUpdates(v => !v)}
            style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
          >
            {showAllUpdates ? "Show less ↑" : `+${(selectedItem as any).updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                  </div>
                </div>
              </ScrollArea>

              <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                <Button {...qaAttrs("btn-close-view-details")} onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>Close Details</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== ADD / EDIT DIALOG ===== */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent {...qaAttrs("dialog-add-edit")} className="max-w-3xl h-[85vh] flex flex-col overflow-hidden p-0">
          <div {...qaAttrs("dialog-add-edit-header")} className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle {...qaAttrs("dialog-add-edit-title")} className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add ICD Not Allowed" : "Edit ICD Not Allowed"}
              </DialogTitle>
              </DialogHeader>
          </div>

          <div {...qaAttrs("dialog-add-edit-body")} className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
            {/* Section 1 */}
            <SectionCard num={1} title="Basic information" subtitle="Name and status of the relation" automationId={qaId("section-basic-information")}>
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Label {...qaAttrs("label-relation-name")} htmlFor={qaId("input-relation-name")}>Relation Name <span className="text-red-500 ml-0.5">*</span></Label>
                  <Input {...qaAttrs("input-relation-name")} value={relationName} onChange={(e) => setRelationName(e.target.value)} placeholder="Must be at least 3 characters" className="mt-1" />
                </div>
                <div className="w-48 flex-shrink-0">
                  <Label {...qaAttrs("label-status")}>Status</Label>
                  <div {...qaAttrs("status-wrapper")} className="mt-1">
                    <StatusToggle automationId={qaId("status-toggle-relation")} label="Group Status" dotColor="#2563eb" value={isActive} onChange={setIsActive} />
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Section 2 */}
            <SectionCard num={2} title="ICD configuration" subtitle="Choose one source for each diagnosis" automationId={qaId("section-icd-configuration")}>
              <div className="space-y-5">
                <IcdSourcePicker
                  automationId={qaId("source-picker-diagnosis-one")}
                  title="Diagnosis One"
                  selected={selectedMainOption}
                  onSelect={setSelectedMainOption}
                  counts={{ list_of_icd: mainDiagnoses.length, medical_icd_groups: mainmedicalIcdGroups.length, custom_icd_groups: mainCustomIcdGroups.length }}
                >
                  {selectedMainOption === "list_of_icd" && <div {...qaAttrs("wrapper-diagnosis-one-list-of-icd")}><Listofmedicalcode onChange={setMainDiagnoses} description={IcdDescription} defaultValue={mainDiagnoses} clear={clearMainDiagnoses} /></div>}
                  {selectedMainOption === "medical_icd_groups" && <div {...qaAttrs("wrapper-diagnosis-one-medical-icd-groups")}><ListofMedicalGroup onChange={setMainMedicalIcdGroups} defaultValue={mainmedicalIcdGroups} clear={clearMainMedicalIcdGroups} /></div>}
                  {selectedMainOption === "custom_icd_groups" && <div {...qaAttrs("wrapper-diagnosis-one-custom-icd-groups")}><ListofCustomIcd onChange={setMainCustomIcdGroups} defaultValue={mainCustomIcdGroups} clear={clearMainCustomIcdGroups} /></div>}
                </IcdSourcePicker>

                <IcdSourcePicker
                  automationId={qaId("source-picker-diagnosis-two")}
                  title="Diagnosis Two"
                  selected={selectedNotAllowedOption}
                  onSelect={setSelectedNotAllowedOption}
                  counts={{ list_of_icd: notAllowedDiagnoses.length, medical_icd_groups: notAllowedmedicalIcdGroups.length, custom_icd_groups: notAllowedcustomIcdGroups.length }}
                >
                  {selectedNotAllowedOption === "list_of_icd" && <div {...qaAttrs("wrapper-diagnosis-two-list-of-icd")}><Listofmedicalcode onChange={setNotAllowedDiagnoses} description={IcdDescription} defaultValue={notAllowedDiagnoses} clear={clearNotAllowedDiagnoses} /></div>}
                  {selectedNotAllowedOption === "medical_icd_groups" && <div {...qaAttrs("wrapper-diagnosis-two-medical-icd-groups")}><ListofMedicalGroup onChange={setNotAllowedMedicalIcdGroups} defaultValue={notAllowedmedicalIcdGroups} clear={clearNotAllowedMedicalIcdGroups} /></div>}
                  {selectedNotAllowedOption === "custom_icd_groups" && <div {...qaAttrs("wrapper-diagnosis-two-custom-icd-groups")}><ListofCustomIcd onChange={setNotAllowedCustomIcdGroups} defaultValue={notAllowedcustomIcdGroups} clear={clearNotAllowedCustomIcdGroups} /></div>}
                </IcdSourcePicker>
              </div>
            </SectionCard>

            {/* Section 3 – Entities */}
            <div {...qaAttrs("section-entities-configuration")} className="border-2 border-blue-200 rounded-xl overflow-hidden mb-4 shadow-sm">
              <div {...qaAttrs("section-entities-configuration-header")} className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">3</span>
                  <div>
                    <p {...qaAttrs("section-entities-configuration-title")} className="text-sm font-semibold text-blue-900">Entities Configuration</p>
                  </div>
                </div>
                <button
                  {...qaAttrs("toggle-entities-configuration")}
                  data-qa-selected={showEntities ? "true" : "false"}
                  type="button"
                  onClick={() => { if (showEntities) resetAllEntityStates(); setShowEntities(!showEntities); }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showEntities ? "bg-green-500" : "bg-gray-300"}`}
                >
                  <span {...qaAttrs("toggle-entities-configuration-knob")} className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showEntities ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>

              {showEntities && (
                <div className="p-5 bg-blue-50">
                  <div className="flex gap-3 p-3 bg-blue-100 border-l-4 border-blue-500 rounded-r-lg mb-4">
                    <svg className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    <p className="text-xs text-blue-900 leading-relaxed">
                      <span className="font-semibold text-emerald-700">Inclusion</span> = apply only to selected items &nbsp;·&nbsp;
                      <span className="font-semibold text-red-700">Exclusion</span> = apply to everyone except selected items
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <EntityCard automationId={qaId("entity-card-providers")} label="Providers" icon="fa-hospital" color="blue" isActive={isProviderActive} mode={providerMode}
                      onExclusion={() => { if (providerMode !== "EXCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); } setProviderMode("EXCLUSION"); setIsProviderActive(true); }}
                      onInclusion={() => { if (providerMode !== "INCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); } setProviderMode("INCLUSION"); setIsProviderActive(true); }}
                      onClear={() => { setProvider([]); setProviderMode(null); setIsProviderActive(false); setClearProvider(true); setSelectAllProviders(false); }}>
                      <label {...qaAttrs("label-select-all-providers")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-providers")} type="checkbox" checked={selectAllProviders} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllProviders(c); if (c) { setProvider([]); setClearProvider(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-blue-600" />
                        <span {...qaAttrs("label-text-select-all-providers")} className="text-sm font-medium text-slate-600">Select All Providers</span>
                      </label>
                      {!selectAllProviders && <div {...qaAttrs("wrapper-provider-selector")} style={{ display: "contents" }}><ListOfProviderName onChange={setProvider} defaultValue={provider} clear={clearProvider} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-doctor")} label="Doctor" icon="fa-stethoscope" color="cyan" isActive={isDoctorActive} mode={doctorMode}
                      onExclusion={() => { if (doctorMode !== "EXCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); } setDoctorMode("EXCLUSION"); setIsDoctorActive(true); }}
                      onInclusion={() => { if (doctorMode !== "INCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); } setDoctorMode("INCLUSION"); setIsDoctorActive(true); }}
                      onClear={() => { setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setClearDoctor(true); setSelectAllDoctor(false); }}>
                      <label {...qaAttrs("label-select-all-doctor")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-doctor")} type="checkbox" checked={selectAllDoctor} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllDoctor(c); if (c) { setDoctor([]); setClearDoctor(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-cyan-600" />
                        <span {...qaAttrs("label-text-select-all-doctor")} className="text-sm font-medium text-slate-600">Select All Doctors</span>
                      </label>
                      {!selectAllDoctor && <div {...qaAttrs("wrapper-doctor-selector")} style={{ display: "contents" }}><ListOfDoctorName onChange={setDoctor} defaultValue={doctor} clear={clearDoctor} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-specialty")} label="Specialty" icon="fa-user-doctor" color="teal" isActive={isSpecialtyActive} mode={specialtyMode}
                      onExclusion={() => { if (specialtyMode !== "EXCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); } setSpecialtyMode("EXCLUSION"); setIsSpecialtyActive(true); }}
                      onInclusion={() => { if (specialtyMode !== "INCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); } setSpecialtyMode("INCLUSION"); setIsSpecialtyActive(true); }}
                      onClear={() => { setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setClearSpecialty(true); setSelectAllSpecialty(false); }}>
                      <label {...qaAttrs("label-select-all-specialty")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-specialty")} type="checkbox" checked={selectAllSpecialty} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllSpecialty(c); if (c) { setSpecialty([]); setClearSpecialty(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-teal-600" />
                        <span {...qaAttrs("label-text-select-all-specialty")} className="text-sm font-medium text-slate-600">Select All Specialties</span>
                      </label>
                      {!selectAllSpecialty && <div {...qaAttrs("wrapper-specialty-selector")} style={{ display: "contents" }}><ListOfSpecialtyName onChange={setSpecialty} defaultValue={specialty} clear={clearSpecialty} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-networks")} label="Networks" icon="fa-network-wired" color="purple" isActive={isNetworkActive} mode={networkMode}
                      onExclusion={() => { if (networkMode !== "EXCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); } setNetworkMode("EXCLUSION"); setIsNetworkActive(true); }}
                      onInclusion={() => { if (networkMode !== "INCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); } setNetworkMode("INCLUSION"); setIsNetworkActive(true); }}
                      onClear={() => { setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setClearNetworks(true); setSelectAllNetworks(false); }}>
                      <label {...qaAttrs("label-select-all-networks")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-networks")} type="checkbox" checked={selectAllNetworks} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllNetworks(c); if (c) { setNetworks([]); setClearNetworks(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-purple-600" />
                        <span {...qaAttrs("label-text-select-all-networks")} className="text-sm font-medium text-slate-600">Select All Networks</span>
                      </label>
                      {!selectAllNetworks && <div {...qaAttrs("wrapper-network-selector")} style={{ display: "contents" }}><ListOfNewNetworkName onChange={setNetworks} defaultValue={networks} clear={clearNetworks} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-insurance-plan")} label="Insurance Plan" icon="fa-shield-halved" color="amber" isActive={isInsuranceActive} mode={insuranceMode}
                      onExclusion={() => { if (insuranceMode !== "EXCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); } setInsuranceMode("EXCLUSION"); setIsInsuranceActive(true); }}
                      onInclusion={() => { if (insuranceMode !== "INCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); } setInsuranceMode("INCLUSION"); setIsInsuranceActive(true); }}
                      onClear={() => { setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setClearInsuranceplan(true); setSelectAllInsurance(false); }}>
                      <label {...qaAttrs("label-select-all-insurance-plan")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-insurance-plan")} type="checkbox" checked={selectAllInsurance} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllInsurance(c); if (c) { setInsuranceplan([]); setClearInsuranceplan(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-amber-600" />
                        <span {...qaAttrs("label-text-select-all-insurance-plan")} className="text-sm font-medium text-slate-600">Select All Insurance Plans</span>
                      </label>
                      {!selectAllInsurance && <div {...qaAttrs("wrapper-insurance-plan-selector")} style={{ display: "contents" }}><ListOfInsuranceplanName onChange={setInsuranceplan} defaultValue={insuranceplan} clear={clearInsuranceplan} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-policy")} label="Policy" icon="fa-file-lines" color="emerald" isActive={isPolicyActive} mode={policyMode}
                      onExclusion={() => { if (policyMode !== "EXCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); } setPolicyMode("EXCLUSION"); setIsPolicyActive(true); }}
                      onInclusion={() => { if (policyMode !== "INCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); } setPolicyMode("INCLUSION"); setIsPolicyActive(true); }}
                      onClear={() => { setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setClearPolicy(true); setSelectAllPolicy(false); }}>
                      <label {...qaAttrs("label-select-all-policy")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-policy")} type="checkbox" checked={selectAllPolicy} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPolicy(c); if (c) { setPolicy([]); setClearPolicy(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
                        <span {...qaAttrs("label-text-select-all-policy")} className="text-sm font-medium text-slate-600">Select All Policies</span>
                      </label>
                      {!selectAllPolicy && <div {...qaAttrs("wrapper-policy-selector")} style={{ display: "contents" }}><PolicyNames onChange={(v: PolicyItem[]) => setPolicy(v)} defaultValue={policy} clear={clearPolicy} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-patients")} label="Patients" icon="fa-users" color="pink" isActive={isPatientActive} mode={patientMode}
                      onExclusion={() => { if (patientMode !== "EXCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); } setPatientMode("EXCLUSION"); setIsPatientActive(true); }}
                      onInclusion={() => { if (patientMode !== "INCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); } setPatientMode("INCLUSION"); setIsPatientActive(true); }}
                      onClear={() => { setPatients([]); setPatientMode(null); setIsPatientActive(false); setClearPatient(true); setSelectAllPatients(false); }}>
                      <label {...qaAttrs("label-select-all-patients")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-patients")} type="checkbox" checked={selectAllPatients} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPatients(c); if (c) { setPatients([]); setClearPatient(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-pink-600" />
                        <span {...qaAttrs("label-text-select-all-patients")} className="text-sm font-medium text-slate-600">Select All Patients</span>
                      </label>
                      {!selectAllPatients && <div {...qaAttrs("wrapper-patient-selector")} style={{ display: "contents" }}><VipPatientSelector onChange={setPatients} defaultValue={patients} clear={clearPatient} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-vip")} label="VIP" icon="fa-star" color="yellow" isActive={isVipActive} mode={vipMode}
                      onExclusion={() => { if (vipMode !== "EXCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); } setVipMode("EXCLUSION"); setIsVipActive(true); }}
                      onInclusion={() => { if (vipMode !== "INCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); } setVipMode("INCLUSION"); setIsVipActive(true); }}
                      onClear={() => { setVip([]); setVipMode(null); setIsVipActive(false); setClearVip(true); setSelectAllVip(false); }}>
                      <label {...qaAttrs("label-select-all-vip")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-vip")} type="checkbox" checked={selectAllVip} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllVip(c); if (c) { setVip([]); setClearVip(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-yellow-600" />
                        <span {...qaAttrs("label-text-select-all-vip")} className="text-sm font-medium text-slate-600">Select All VIP</span>
                      </label>
                      {!selectAllVip && <div {...qaAttrs("wrapper-vip-selector")} style={{ display: "contents" }}><Vip onChange={setVip} defaultValue={vip} clear={clearVip} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-payer")} label="Payer" icon="fa-building-columns" color="indigo" isActive={isPayerActive} mode={payerMode}
                      onExclusion={() => { if (payerMode !== "EXCLUSION") { setPayer([]); setSelectAllPayer(false); } setPayerMode("EXCLUSION"); setIsPayerActive(true); }}
                      onInclusion={() => { if (payerMode !== "INCLUSION") { setPayer([]); setSelectAllPayer(false); } setPayerMode("INCLUSION"); setIsPayerActive(true); }}
                      onClear={() => { setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false); }}>
                      <label {...qaAttrs("label-select-all-payer")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-payer")} type="checkbox" checked={selectAllPayer} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPayer(c); if (c) setPayer([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-indigo-600" />
                        <span {...qaAttrs("label-text-select-all-payer")} className="text-sm font-medium text-slate-600">Select All Payers</span>
                      </label>
                      {!selectAllPayer && <Input {...qaAttrs("input-payer")} value={Payer.join(",")} onChange={(e) => setPayer([e.target.value])} placeholder="Enter Payer" />}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-user-level")} label="User Level" icon="fa-user-gear" color="slate" isActive={isUserActive} mode={userMode}
                      onExclusion={() => { if (userMode !== "EXCLUSION") { setUserLevel([]); setSelectAllUser(false); } setUserMode("EXCLUSION"); setIsUserActive(true); }}
                      onInclusion={() => { if (userMode !== "INCLUSION") { setUserLevel([]); setSelectAllUser(false); } setUserMode("INCLUSION"); setIsUserActive(true); }}
                      onClear={() => { setUserLevel([]); setUserMode(null); setIsUserActive(false); setSelectAllUser(false); }}>
                      <label {...qaAttrs("label-select-all-user-level")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-user-level")} type="checkbox" checked={selectAllUser} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllUser(c); if (c) setUserLevel([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-slate-600" />
                        <span {...qaAttrs("label-text-select-all-user-level")} className="text-sm font-medium text-slate-600">Select All User Levels</span>
                      </label>
                      {!selectAllUser && <Input {...qaAttrs("input-user-level")} value={userLevel.join(",")} onChange={(e) => setUserLevel([e.target.value])} placeholder="Enter User Level" />}
                    </EntityCard>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div {...qaAttrs("dialog-add-edit-footer")} className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button {...qaAttrs("btn-cancel-add-edit-dialog")} data-qa-action="cancel-add-edit" variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}>Cancel</Button>
            <Button
              id={qaId(editIndex === null ? "btn-add-relation-submit" : "btn-update-relation-submit")}
              data-testid={qaId("btn-save-relation")}
              data-qa={qaId(editIndex === null ? "btn-add-relation-submit" : "btn-update-relation-submit")}
              data-qa-action={editIndex === null ? "add-relation" : "update-relation"}
              onClick={() => editIndex === null ? addItem() : updateItem(editIndex)}
              className="bg-blue-700 hover:bg-blue-800 text-white"
            >
              {editIndex === null ? "Add Relation " : "Update Relation "}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* All Codes Dialog */}
      <Dialog open={allCodesDialogOpen} onOpenChange={setAllCodesDialogOpen}>
        <DialogContent {...qaAttrs("dialog-all-codes")} className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle {...qaAttrs("dialog-all-codes-title")}>
              <span {...qaAttrs("dialog-all-codes-title-text")}>{allCodesDialogTitle}</span>
              <span {...qaAttrs("dialog-all-codes-count")}> ({allCodesDialogList.length})</span>
            </DialogTitle>
          </DialogHeader>
          <div {...qaAttrs("dialog-all-codes-body")} className="mt-4 overflow-y-auto max-h-[calc(85vh-140px)]">
            <div className="flex flex-wrap gap-2">
              {allCodesDialogList.map((code, i) => {
                const isCustom = code.startsWith("[Custom]");
                const isMedical = code.startsWith("[Medical]");
                const bg = isMedical ? "#f0fdf4" : isCustom ? "#faf5ff" : "#eff6ff";
                const border = isMedical ? "#86efac" : isCustom ? "#d8b4fe" : "#bfdbfe";
                const color = isMedical ? "#166534" : isCustom ? "#6b21a8" : "#1d4ed8";
                const typeLabel = code.match(/\[(.+?)\]/)?.[1] ?? "ICD";
                const label = code.replace(/\[.+?\] /, "");
                return (
                  <div
                    key={i}
                    id={qaId(`dialog-all-codes-item-${i}`)}
                    data-testid={qaId(`dialog-all-codes-item-${i}`)}
                    data-qa={qaId(`dialog-all-codes-item-${i}`)}
                    data-qa-label={label}
                    data-qa-type={typeLabel}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 9999, margin: "2px", background: bg, border: `1px solid ${border}` }}
                  >
                    <span id={qaId(`dialog-all-codes-item-${i}-type`)} data-testid={qaId(`dialog-all-codes-item-${i}-type`)} data-qa={qaId(`dialog-all-codes-item-${i}-type`)} style={{ fontSize: 9, fontWeight: 700, color, opacity: 0.7 }}>{typeLabel}</span>
                    <span id={qaId(`dialog-all-codes-item-${i}-label`)} data-testid={qaId(`dialog-all-codes-item-${i}-label`)} data-qa={qaId(`dialog-all-codes-item-${i}-label`)} style={{ fontSize: 12, fontWeight: 500, color }}>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t mt-4">
            <Button {...qaAttrs("btn-close-all-codes-dialog")} onClick={() => setAllCodesDialogOpen(false)} className="bg-blue-600 hover:bg-blue-700">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Force / Hard Delete Dialog ───────────────────────────────────────── */}
      {forceDialogOpen && (
        <div {...qaAttrs("dialog-force-delete-overlay")} style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div
            {...qaAttrs("dialog-force-delete")}
            data-qa-force-action={forceAction ?? ""}
            data-qa-force-id={forceId ?? ""}
            style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}
          >
            <div {...qaAttrs("dialog-force-delete-content")} style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div {...qaAttrs("delete-alert-icon-wrapper")} style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i {...qaAttrs("delete-alert-icon")} className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {/* Hard Delete */}
              {forceAction === "hardDelete" && (
                <>
                  <h3 {...qaAttrs("hard-delete-alert-title")} style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p {...qaAttrs("hard-delete-alert-message")} style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the record.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div {...qaAttrs("hard-delete-error-container")} style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i {...qaAttrs("hard-delete-error-icon")} className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p {...qaAttrs("hard-delete-error-message")} style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                <div {...qaAttrs("hard-delete-actions")} style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
  <button
    {...qaAttrs("btn-cancel-hard-delete")}
    data-qa-action="cancel-hard-delete"
    onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
    style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
    Cancel
  </button>
  <button
    {...qaAttrs("btn-confirm-hard-force-delete")}
    data-qa-action="confirm-hard-force-delete"
    onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
    style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
    <i {...qaAttrs("icon-confirm-hard-force-delete")} className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
  </button>
</div>
                </>
              )}

              {forceAction === "softDelete" && (
                <>
                  <h3 {...qaAttrs("soft-delete-alert-title")} style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Record Is In Use</h3>
                  <div {...qaAttrs("soft-delete-error-container")} style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i {...qaAttrs("soft-delete-error-icon")} className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p {...qaAttrs("soft-delete-error-message")} style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p {...qaAttrs("soft-delete-confirmation-message")} style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force delete</strong> anyway?
                  </p>
                  <div {...qaAttrs("soft-delete-actions")} style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button {...qaAttrs("btn-cancel-soft-delete")} data-qa-action="cancel-soft-delete" onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button {...qaAttrs("btn-confirm-soft-force-delete")} data-qa-action="confirm-soft-force-delete" onClick={() => { if (forceId) forceSoftDelete(forceId); }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i {...qaAttrs("icon-confirm-soft-force-delete")} className="fa-solid fa-bolt mr-2"></i>Force Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div {...qaAttrs("toaster-wrapper")}>
        <Toaster />
      </div>
    </div>
  );
};

export default IcdNotAllowed;